import React from "react";
import styles from "./styles.module.scss";
import clsx from "clsx";
import { useChatLog } from "../../../contexts/chat-log";
import { useChatbotConfig } from "../../../contexts/chatbot-config";
import { sleep } from "../../../libs/fns";

export default function ChatbotButton({
  className,
  open,
  setOpen,
}: {
  className?: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { chatLogs } = useChatLog();
  const { chatbotData } = useChatbotConfig();
  const [initialized, setInitialized] = React.useState(false);
  const [displayContent, setDisplayContent] = React.useState<'image' | 'emoji' | null>(null);
  const [preloadedImageUrl, setPreloadedImageUrl] = React.useState<string | null>(null);
  // 初期マウント時のデバイスタイプを保存（PCの場合はリサイズで変更されない）
  const [isMobile] = React.useState(() => window.innerWidth <= 768);

  async function handleClick() {
    setOpen(true);

    if (chatLogs.length === 0) {
      await sleep(0.8);
    }
  }

  // 位置とサイズをメモ化（PCの場合はブラウザリサイズに関係なく固定）
  const positionStyle = React.useMemo(() => {
    const style: React.CSSProperties = {
      // デフォルト値を設定
      bottom: '24px',
      right: '24px',
      width: '64px',
      height: '64px'
    };

    // 位置情報の設定（PC/モバイルで別々の位置値を使用）
    // isMobileは初期マウント時に固定されるため、PCの場合はリサイズしても変更されない
    if (isMobile) {
      // モバイル用の位置設定（通常フィールドを使用）
      if (chatbotData?.chatbot_icon_position_x !== undefined) {
        style.right = `${chatbotData.chatbot_icon_position_x}px`;
      }

      if (chatbotData?.chatbot_icon_position_y !== undefined) {
        style.bottom = `${chatbotData.chatbot_icon_position_y}px`;
      }
    } else {
      // PC用の位置設定（通常フィールドを使用）
      // 初期ロード時にPCと判定された場合、ブラウザリサイズしても変更されない
      if (chatbotData?.chatbot_icon_position_x !== undefined) {
        style.right = `${chatbotData.chatbot_icon_position_x}px`;
      }
      if (chatbotData?.chatbot_icon_position_y !== undefined) {
        style.bottom = `${chatbotData.chatbot_icon_position_y}px`;
      }
    }

    // サイズ情報の設定（PC/モバイル共通）
    if (chatbotData?.chatbot_icon_width !== undefined) {
      style.width = `${chatbotData.chatbot_icon_width}px`;
    }
    if (chatbotData?.chatbot_icon_height !== undefined) {
      style.height = `${chatbotData.chatbot_icon_height}px`;
    }

    return style;
  }, [chatbotData, isMobile]); // isMobileは初期値から変わらないため、PC判定された場合は固定

  React.useEffect(() => {
    // chatbotDataは初回マウント時とアイドルリセット時の2回設定され得るため、
    // 先に開始した(遅い)プリロードのonload/onerrorが後から解決して、
    // 後で設定された(速い)方の表示結果を上書きしないようにする
    let cancelled = false;

    if (chatbotData === undefined || chatbotData === null) {
      setInitialized(false);
      setDisplayContent(null);
      return () => {
        cancelled = true;
      };
    }

    // chatbot_icon_pathを優先的に使用、なければchatbot_iconを使用 (下位互換性)
    const iconPath = chatbotData.chatbot_icon_path || chatbotData.chatbot_icon;

    if (iconPath) {
      setInitialized(false);
      setDisplayContent(null);
      setPreloadedImageUrl(null);

      // chatbot_icon_pathの場合、S3 URLと結合
      let iconUrl: string;
      if (chatbotData.chatbot_icon_path && !chatbotData.chatbot_icon_path.startsWith('http')) {
        const baseUrl = chatbotData.chatbot_S3_URL || '';
        const encodedPath = chatbotData.chatbot_icon_path.split('/').map(part => encodeURIComponent(part)).join('/');
        iconUrl = `${baseUrl}/${encodedPath}`;
      } else {
        iconUrl = iconPath;
      }

      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        setPreloadedImageUrl(iconUrl);
        setDisplayContent('image');
        setInitialized(true);
      };
      img.onerror = () => {
        if (cancelled) return;
        setPreloadedImageUrl(null);
        setDisplayContent('emoji');
        setInitialized(true);
      };
      img.src = iconUrl;
    } else {
      setPreloadedImageUrl(null);
      setDisplayContent('emoji');
      setInitialized(true);
    }

    return () => {
      cancelled = true;
    };
  }, [chatbotData]);

  // チャットが開いている間はボタンを非表示
  if (!initialized || displayContent === null || open) {
    return null;
  }

  return (
    <button
      className={clsx(styles.button, className)}
      onClick={handleClick}
      type="button"
      style={positionStyle}
    >
      {displayContent === 'image' && preloadedImageUrl ? (
        <img
          src={preloadedImageUrl}
          alt="Chatbot"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'fill',
            borderRadius: `${chatbotData?.chatbot_icon_radius ?? 0}px`
          }}
        />
      ) : (
        <span>Chat</span>
      )}
    </button>
  );
}
